import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Video, Calendar, DollarSign, ArrowRight, ShoppingCart } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT = {
  hero_headline: 'Individual Options',
  hero_description: 'Purchase individual study materials or book one-on-one tutoring sessions tailored to your needs.',
  materials_title: 'Individual Study Materials',
  materials_desc: 'Purchase specific videos, PDFs, practice tests, or interactive modules à la carte',
  materials_note: 'After purchase you will receive your materials via email.',
  individual_items: [],
  sessions_title: 'One-on-One Tutoring Sessions',
  sessions_desc: 'Schedule personalized tutoring sessions with expert instructors',
  sessions_list: '30-minute sessions: $50\n60-minute sessions: $90\n90-minute sessions: $130\nPackage deals available',
  sessions_note: 'Get personalized guidance, ask questions, and work through challenging concepts with an expert.',
  sessions_booking_url: '',
  cta_headline: 'Ready to Get Started?',
  cta_description: 'Browse our materials or book a session to get started.'
};

export const PrepIndividual = () => {
  const [c, setC] = useState(DEFAULT);

  useEffect(() => {
    axios.get(`${API}/prep/content/prep-individual`)
      .then(r => setC({ ...DEFAULT, ...r.data, individual_items: r.data.individual_items || [] }))
      .catch(console.error);
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">{c.hero_headline}</h1>
          <p className="text-xl text-blue-100 max-w-3xl">{c.hero_description}</p>
        </div>
      </section>

      {/* Two Cards */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

            {/* Materials Card */}
            <Card className="border-2 hover:shadow-xl transition-all">
              <CardHeader>
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <BookOpen className="text-blue-600" size={32} />
                </div>
                <CardTitle className="text-2xl">{c.materials_title}</CardTitle>
                <CardDescription className="text-base">{c.materials_desc}</CardDescription>
              </CardHeader>
              <CardContent>
                {c.individual_items.length > 0 ? (
                  <div className="space-y-3">
                    {c.individual_items.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100">
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{item.title}</p>
                          {item.price && <p className="text-blue-600 font-bold text-sm">{item.price}</p>}
                        </div>
                        {item.payment_url ? (
                          <a href={item.payment_url} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                              <ShoppingCart size={14} className="mr-1" /> Purchase
                            </Button>
                          </a>
                        ) : (
                          <Button size="sm" variant="outline" className="border-blue-600 text-blue-600" disabled>
                            Coming Soon
                          </Button>
                        )}
                      </div>
                    ))}
                    {c.materials_note && (
                      <p className="text-xs text-gray-500 mt-3">{c.materials_note}</p>
                    )}
                  </div>
                ) : (
                  <div className="bg-blue-50 rounded-lg p-6 text-center">
                    <p className="text-gray-500 text-sm">Individual materials coming soon.</p>
                    <p className="text-gray-400 text-xs mt-2">Check back for available study resources.</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Sessions Card */}
            <Card className="border-2 hover:shadow-xl transition-all">
              <CardHeader>
                <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                  <Calendar className="text-green-600" size={32} />
                </div>
                <CardTitle className="text-2xl">{c.sessions_title}</CardTitle>
                <CardDescription className="text-base">{c.sessions_desc}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">Session Options:</h4>
                    <ul className="space-y-2 text-sm text-gray-700">
                      {(c.sessions_list || '').split('\n').filter(l => l.trim()).map((item, i) => (
                        <li key={i}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                  {c.sessions_note && <p className="text-sm text-gray-600">{c.sessions_note}</p>}
                  {c.sessions_booking_url ? (
                    <a href={c.sessions_booking_url} target="_blank" rel="noopener noreferrer">
                      <Button className="w-full bg-green-600 hover:bg-green-700 text-white">
                        Book a Session <ArrowRight className="ml-2" size={16} />
                      </Button>
                    </a>
                  ) : (
                    <Link to="/contact">
                      <Button className="w-full bg-green-600 hover:bg-green-700 text-white">
                        Contact to Book <ArrowRight className="ml-2" size={16} />
                      </Button>
                    </Link>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Why Choose Individual Options?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: DollarSign, title: 'Pay for What You Need', desc: 'No need to purchase full programs. Buy only the materials or sessions you need.' },
              { icon: Video, title: 'Flexible Learning', desc: 'Study at your own pace with access to purchased materials.' },
              { icon: Calendar, title: 'Personalized Support', desc: 'Get one-on-one attention from experienced instructors when you need it most.' }
            ].map(({ icon: Icon, title, desc }) => (
              <Card key={title} className="border-2">
                <CardHeader>
                  <Icon className="text-blue-600 mb-2" size={32} />
                  <CardTitle className="text-xl">{title}</CardTitle>
                </CardHeader>
                <CardContent><p className="text-gray-600">{desc}</p></CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">{c.cta_headline}</h2>
          <p className="text-xl text-blue-100 mb-8">{c.cta_description}</p>
          <Link to="/prep/programs">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 text-lg px-8 py-6">
              View Full Programs <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
